/**
 * Order Management - Shared Components
 * Reusable UI primitives for the entire Order Management module.
 * Uses the LexiBridge brand teal (#45B7AA) palette throughout.
 *
 * client/src/components/pages/OrderManagement/shared/index.tsx (converted from .js, ST-00216)
 *
 * ST-00227 (audit ORD-16): the "Base Layer" sync badge, ping-pong visualiser and sync panel
 * were removed — they only ever showed placeholder figures. InlineAlert shows a failed call.
 */

import type { ComponentType, ReactNode } from 'react';
import { ArrowRight, AlertCircle, X } from 'lucide-react';
import type {
  CompanyId,
  HaulierChoice,
  OrderCompany,
} from '../../../../types/clientPages/orderManagement';

/** A style table keyed by a server value, with the entry used for unknown values. */
type StyleTable<T, Fallback extends string> = Record<string, T | undefined> & Record<Fallback, T>;

interface BadgeStyle {
  bg: string;
  text: string;
  border: string;
}

/* ─────────────────────────────────────────────
   STATUS BADGE
   ───────────────────────────────────────────── */
const statusConfig: StyleTable<BadgeStyle & { dot: string }, 'On Hold'> = {
  Active: {
    bg: 'bg-primary-100',
    text: 'text-primary-800',
    border: 'border-primary-200',
    dot: 'bg-primary-500',
  },
  'In Progress': {
    bg: 'bg-amber-50',
    text: 'text-amber-800',
    border: 'border-amber-200',
    dot: 'bg-amber-500',
  },
  Completed: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-800',
    border: 'border-emerald-200',
    dot: 'bg-emerald-500',
  },
  Cancelled: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
  'On Hold': {
    bg: 'bg-slate-100',
    text: 'text-slate-700',
    border: 'border-slate-200',
    dot: 'bg-slate-400',
  },
};

export interface StatusBadgeProps {
  status: string;
  size?: 'sm' | 'lg';
}

export const StatusBadge = ({ status, size = 'sm' }: StatusBadgeProps) => {
  const config = statusConfig[status] || statusConfig['On Hold'];
  const sizeClasses = size === 'lg' ? 'px-3 py-1.5 text-sm' : 'px-2.5 py-1 text-xs';

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold border ${sizeClasses} ${config.bg} ${config.text} ${config.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {status}
    </span>
  );
};

/* ─────────────────────────────────────────────
   PRIORITY BADGE
   ───────────────────────────────────────────── */
const priorityConfig: StyleTable<BadgeStyle & { icon: string | null }, 'Normal'> = {
  Urgent: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: '!!' },
  High: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', icon: '!' },
  Normal: {
    bg: 'bg-primary-50',
    text: 'text-primary-700',
    border: 'border-primary-200',
    icon: null,
  },
  Low: { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', icon: null },
};

export const PriorityBadge = ({ priority }: { priority: string }) => {
  const config = priorityConfig[priority] || priorityConfig['Normal'];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border ${config.bg} ${config.text} ${config.border}`}
    >
      {config.icon && <span className="font-bold">{config.icon}</span>}
      {priority}
    </span>
  );
};

/* ─────────────────────────────────────────────
   LOADING SKELETON
   ───────────────────────────────────────────── */
export const Skeleton = ({
  className = '',
  variant = 'rect',
}: {
  className?: string;
  variant?: 'rect' | 'circle';
}) => {
  const baseClasses = 'animate-pulse bg-slate-200 rounded';
  if (variant === 'circle') {
    return <div className={`${baseClasses} rounded-full ${className}`} />;
  }
  return <div className={`${baseClasses} ${className}`} />;
};

export const TableSkeleton = ({ rows = 5, cols = 6 }: { rows?: number; cols?: number }) => (
  <div className="bg-surface rounded-xl border border-slate-200 overflow-hidden">
    <div className="px-5 py-4 border-b border-slate-100">
      <Skeleton className="h-5 w-40" />
    </div>
    <div className="divide-y divide-slate-100">
      {[...Array(rows)].map((_, i) => (
        <div
          key={i}
          className="px-5 py-4 flex items-center gap-6"
          style={{ animationDelay: `${i * 60}ms` }}
        >
          {[...Array(cols)].map((_, j) => (
            <Skeleton
              key={j}
              className={`h-4 ${j === 0 ? 'w-24' : j === cols - 1 ? 'w-16' : 'w-20'}`}
            />
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const KPISkeleton = () => (
  <div className="bg-surface rounded-xl border border-slate-200 p-5">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <Skeleton className="h-3 w-24 mb-3" />
        <Skeleton className="h-8 w-16 mb-2" />
        <Skeleton className="h-3 w-32" />
      </div>
      <Skeleton className="w-10 h-10 rounded-lg" />
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   HAULIER DROPDOWN CHOICES
   ───────────────────────────────────────────── */
/**
 * Companies offered in a Haulier dropdown: active companies of type Hauler,
 * plus the record's current haulier when it is not one of those (an Internal
 * fleet company, or a deactivated haulier — 111 vehicles and 162 drivers on the
 * prod copy). Without it the dropdown showed "Select haulier..." for a record
 * that does have one. The extra entry is labelled so it is not mistaken for a
 * normal choice. ST-00215 / ORD-1.
 */
export function haulierChoices(
  companies: OrderCompany[],
  currentId: CompanyId | '' | null | undefined,
  currentName?: string | null
): HaulierChoice[] {
  const active = companies.filter((c) => c.company_type === 'Hauler' && c.active_status);
  const choices = active.map((c) => ({ value: c.company_id, label: c.company_name }));
  if (currentId == null || currentId === '') return choices;
  if (active.some((c) => String(c.company_id) === String(currentId))) return choices;
  const current = companies.find((c) => String(c.company_id) === String(currentId));
  const name = current?.company_name || currentName || `Company #${currentId}`;
  const note = !current ? 'unknown' : !current.active_status ? 'inactive' : current.company_type;
  return [
    { value: current ? current.company_id : currentId, label: `${name} (${note})` },
    ...choices,
  ];
}

/* ─────────────────────────────────────────────
   EMPTY STATE
   ───────────────────────────────────────────── */
export interface EmptyStateProps {
  icon: ComponentType<{ className?: string }>;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  onAction?: () => void;
}

export const EmptyState = ({
  icon: Icon,
  title,
  description,
  action,
  onAction,
}: EmptyStateProps) => (
  <div className="flex flex-col items-center justify-center py-16 px-6">
    <div className="relative mb-5">
      <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center">
        <Icon className="w-7 h-7 text-slate-400" />
      </div>
      <div className="absolute -inset-1 rounded-2xl bg-slate-200/50 animate-ripple" />
    </div>
    <h3
      className="text-base font-bold text-slate-800 mb-1"
      style={{ fontFamily: 'DM Sans, sans-serif' }}
    >
      {title}
    </h3>
    <p className="text-sm text-slate-500 mb-5 text-center max-w-sm">{description}</p>
    {action && onAction && (
      <button
        onClick={onAction}
        className="px-4 py-2 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
      >
        {action}
        <ArrowRight className="w-4 h-4" />
      </button>
    )}
  </div>
);

/* ─────────────────────────────────────────────
   INLINE ALERT  (a failed call, ST-00227)
   ───────────────────────────────────────────── */
export interface InlineAlertProps {
  children: ReactNode;
  onDismiss?: () => void;
  className?: string;
}

export const InlineAlert = ({ children, onDismiss, className = '' }: InlineAlertProps) => (
  <div
    role="alert"
    className={`flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 ${className}`}
  >
    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
    <div className="flex-1 min-w-0">{children}</div>
    {onDismiss && (
      <button
        type="button"
        onClick={onDismiss}
        className="p-0.5 rounded hover:bg-red-100 text-red-500 transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    )}
  </div>
);
